import { User } from "@fairfleet/geotab";
import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { GridColDef, GridRenderCellParams, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAllUsers } from "./hooks/useAllUsers";
import { useUpdateIbisId } from "./hooks/useUpdateIbisId";
import { IbisIdData, IBIS_ID_ADDIN_ID } from "./models/IbisIdData";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_USER } from "@/constants/resultsLimits";
import { useRawAddinData } from "@/hooks/addins/useAddinData";

interface IbisIdRow {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  name: string;
  isDriver: boolean;
  ibisId: string;
}

function IbisIdToolbar() {
  return <GridToolbarQuickFilter debounceMs={300} />;
}

function buildRows(users: User[] | undefined, addinData: IbisIdData[]): IbisIdRow[] {
  if (!users) return [];

  const addinMap = new Map<string, string>();
  for (const entry of addinData) {
    addinMap.set(entry.userId, entry.value);
  }

  return users.map(user => ({
    id: user.id,
    userId: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    name: user.name,
    isDriver: user.isDriver,
    ibisId: addinMap.get(user.id) ?? "",
  }));
}

function EditableIbisIdCell({
  row,
  onSave,
  isSaving,
}: {
  row: IbisIdRow;
  onSave: (row: IbisIdRow, newValue: string) => void;
  isSaving: boolean;
}) {
  const [value, setValue] = useState(row.ibisId);
  const hasChanged = value !== row.ibisId;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
      <TextField
        size="small"
        value={value}
        onChange={e => setValue(e.target.value)}
        variant="standard"
        sx={{ flex: 1 }}
      />
      {hasChanged && (
        <IconButton size="small" onClick={() => onSave(row, value)} disabled={isSaving}>
          <SaveIcon fontSize="small" color="primary" />
        </IconButton>
      )}
    </Box>
  );
}

export default function IbisIdPage() {
  const { t } = useTranslation();
  const { username } = useGeotabClient();
  const isFairfleetUser = username?.endsWith("@fairfleet.de") ?? false;
  const { data: users, isLoading: usersLoading, isTruncated: usersTruncated } = useAllUsers();
  const { data: addinData = [], isLoading: addinLoading } =
    useRawAddinData<IbisIdData>(IBIS_ID_ADDIN_ID);
  const updateIbisId = useUpdateIbisId();
  const [savingRowId, setSavingRowId] = useState<string | null>(null);

  const addinDetails = useMemo(() => addinData.map(d => d.details), [addinData]);
  const allRows = useMemo(() => buildRows(users, addinDetails), [users, addinDetails]);
  const rows = useMemo(
    () => (isFairfleetUser ? allRows : allRows.filter(row => row.isDriver)),
    [allRows, isFairfleetUser],
  );

  const handleSave = async (row: IbisIdRow, newValue: string) => {
    setSavingRowId(row.id);
    try {
      await updateIbisId.mutateAsync({ userId: row.userId, value: newValue });
    } finally {
      setSavingRowId(null);
    }
  };

  const columns: GridColDef<IbisIdRow>[] = [
    {
      field: "firstName",
      headerName: t("ibisId.firstName"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "lastName",
      headerName: t("ibisId.lastName"),
      flex: 1,
      minWidth: 120,
    },
    {
      field: "name",
      headerName: t("ibisId.username"),
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: "isDriver",
      headerName: t("ibisId.isDriver"),
      width: 100,
      type: "boolean",
    },
    {
      field: "ibisId",
      headerName: t("ibisId.ibisId"),
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<IbisIdRow>) => (
        <EditableIbisIdCell
          row={params.row}
          onSave={handleSave}
          isSaving={savingRowId === params.row.id}
        />
      ),
    },
  ];

  return (
    <Stack sx={{ height: "100%", p: 2, gap: 2 }}>
      {usersTruncated && (
        <Alert severity="warning">
          {t("ibisId.truncated", { count: RESULTS_LIMIT_USER })}
        </Alert>
      )}
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <I18nGrid
          settingsKey="ibisId"
          rows={rows}
          columns={columns}
          loading={usersLoading || addinLoading}
          density="compact"
          initialState={{
            sorting: { sortModel: [{ field: "lastName", sort: "asc" }] },
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: IbisIdToolbar }}
        />
      </Box>
    </Stack>
  );
}
